import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./useAuth";

export interface WalletTransaction {
  id: string;
  user_id: string;
  order_id: string | null;
  amount: number;
  type: string;
  description: string | null;
  created_at: string;
}

export interface WalletData {
  balance: number;
  total_earnings: number;
  pending_profits: number;
  updated_at: string | null;
}

/* ── sessionStorage cache helpers ── */
const walletCacheKey = (userId: string) => `wallet_cache_v1_${userId}`;
const txCacheKey = (userId: string) => `wallet_tx_cache_v1_${userId}`;

function readWalletCache(userId: string): WalletData | null {
  try {
    const raw = sessionStorage.getItem(walletCacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      balance: parsed.balance ?? 0,
      total_earnings: parsed.total_earnings ?? 0,
      pending_profits: parsed.pending_profits ?? 0,
      updated_at: parsed.updated_at ?? null,
    };
  } catch {
    return null;
  }
}

function writeWalletCache(userId: string, data: WalletData) {
  try {
    sessionStorage.setItem(walletCacheKey(userId), JSON.stringify(data));
  } catch {
    // ignore quota exceeded / private mode
  }
}

function readTxCache(userId: string): WalletTransaction[] {
  try {
    const raw = sessionStorage.getItem(txCacheKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeTxCache(userId: string, txs: WalletTransaction[]) {
  try {
    sessionStorage.setItem(txCacheKey(userId), JSON.stringify(txs));
  } catch {
    // ignore
  }
}

export function clearWalletCache(userId: string) {
  try {
    sessionStorage.removeItem(walletCacheKey(userId));
    sessionStorage.removeItem(txCacheKey(userId));
  } catch {
    // ignore
  }
}

export function useWallet() {
  const { user, isLoggedIn } = useAuth();
  const userId = user?.id;

  // ── Cache-first initial state (synchronous, <1ms) ──
  const cachedWallet = userId ? readWalletCache(userId) : null;
  const cachedTxs = userId ? readTxCache(userId) : [];
  const [wallet, setWallet] = useState<WalletData | null>(cachedWallet);
  const [transactions, setTransactions] = useState<WalletTransaction[]>(cachedTxs);
  // If cache hit, user sees data instantly — no loading spinner.
  const [loading, setLoading] = useState(cachedWallet === null);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const prevUserIdRef = useRef<string | null>(null);
  const prevRefreshKeyRef = useRef(0);
  const fetchingRef = useRef(false);

  // ── When userId changes (login / switch user), load that user's cache immediately ──
  useEffect(() => {
    if (!userId) {
      setWallet(null);
      setTransactions([]);
      setLoading(false);
      prevUserIdRef.current = null;
      return;
    }
    const w = readWalletCache(userId);
    const t = readTxCache(userId);
    setWallet(w);
    setTransactions(t);
    setLoading(w === null);
  }, [userId]);

  /* ── Core fetch: background sync, never blocks UI if cache exists ── */
  useEffect(() => {
    if (!isLoggedIn || !userId) return;

    // Deduplicate: already fetched for this exact user + refreshKey
    if (userId === prevUserIdRef.current && refreshKey === prevRefreshKeyRef.current) {
      return;
    }
    prevUserIdRef.current = userId;
    prevRefreshKeyRef.current = refreshKey;

    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setError("");

    const controller = new AbortController();

    const load = async () => {
      try {
        // Parallel fetch — wallet + transactions + dynamic pending-profit simultaneously
        const [walletRes, txRes, pendingRes] = await Promise.all([
          supabase
            .from("wallets")
            .select("balance,updated_at,total_earnings,pending_profits")
            .eq("user_id", userId)
            .maybeSingle(),
          supabase
            .from("wallet_transactions")
            .select("*")
            .eq("user_id", userId)
            .order("created_at", { ascending: false })
            .limit(50),
          // Dynamic pending profit: sum of (profit + extra_profit) * quantity for processing orders
          supabase
            .from("orders")
            .select("profit, extra_profit, quantity")
            .eq("user_id", userId)
            .eq("status", "active"),
        ]);

        if (controller.signal.aborted) return;

        // ── Calculate dynamic pending profits ──
        let dynamicPending = 0;
        if (!pendingRes.error && pendingRes.data) {
          dynamicPending = (pendingRes.data as { profit: number | null; extra_profit: number | null; quantity: number | null }[]).reduce(
            (sum, order) => {
              const profit = order.profit ?? 0;
              const extra = order.extra_profit ?? 0;
              const qty = order.quantity ?? 1;
              return sum + (profit + extra) * qty;
            },
            0
          );
        }

        if (walletRes.error) {
          console.error("[useWallet] fetch wallet error:", walletRes.error);
          setError("錢包資料載入失敗");
        } else if (!walletRes.data) {
          const zeroWallet = { balance: 0, total_earnings: 0, pending_profits: dynamicPending, updated_at: null };
          setWallet(zeroWallet);
          writeWalletCache(userId, zeroWallet);
        } else {
          const newWallet = {
            balance: walletRes.data.balance ?? 0,
            total_earnings: walletRes.data.total_earnings ?? 0,
            pending_profits: dynamicPending,
            updated_at: walletRes.data.updated_at,
          };
          setWallet(newWallet);
          writeWalletCache(userId, newWallet);
        }

        if (txRes.error) {
          console.error("[useWallet] fetch transactions error:", txRes.error);
        } else {
          const txs = (txRes.data as WalletTransaction[]) ?? [];
          setTransactions(txs);
          writeTxCache(userId, txs);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("[useWallet] exception:", err);
        setError("錢包資料載入失敗");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          fetchingRef.current = false;
        }
      }
    };

    load();

    return () => {
      controller.abort();
    };
  }, [userId, isLoggedIn, refreshKey]);

  /* ── Stable refetch (does NOT churn callback references) ── */
  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  /* ── Listen for global refresh events + clear cache so next entry is fresh ── */
  useEffect(() => {
    const handleRefresh = () => {
      if (userId) clearWalletCache(userId);
      refetch();
    };
    window.addEventListener("wallet:refresh", handleRefresh);
    return () => window.removeEventListener("wallet:refresh", handleRefresh);
  }, [refetch, userId]);

  // Supabase Realtime：後台一更新，前台立刻自動刷新
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`wallet_and_orders_${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders", filter: `user_id=eq.${userId}` },
        () => {
          refetch();
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "wallets", filter: `user_id=eq.${userId}` },
        () => {
          refetch();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, refetch]);

  return { wallet, transactions, loading, error, refetch };
}